import { Award } from "lucide-react";
import Reveal from "./Reveal";

const splitLines = (raw) => raw.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => l.split("|").map((s) => s.trim()));

function Seal({ tier, product, score, tone, index }) {
  const silver = tone === "argent";
  return (
    <Reveal delay={index * 250} className="flex flex-col items-center text-center gap-5" data-testid={`story-medal-${index}`}>
      <div className={`wax-seal seal-lazy ${silver ? "wax-seal--silver" : ""}`} style={{ width: 150, height: 150 }}>
        <div className="flex flex-col items-center px-3 text-[#3a1f05]">
          <Award className="w-6 h-6 mb-1" strokeWidth={2.4} />
          <span className="font-display text-[15px] font-bold uppercase tracking-[0.12em] leading-tight">{tier}</span>
          {score && <span className="mt-1 font-display text-2xl font-bold leading-none">{score}</span>}
          <span className="mt-1.5 text-[7px] uppercase tracking-[0.2em] opacity-85 leading-tight">Frankfurt<br />Trophy 2026</span>
        </div>
      </div>
      <div>
        <div className="font-display text-xl md:text-2xl">{product}</div>
        <div className="smallcaps-label mt-1" style={{ fontSize: "0.62rem" }}>Rhum arrangé · {tier}</div>
      </div>
    </Reveal>
  );
}

function JuryBar({ label, value, max, index, silver }) {
  const pct = Math.max(0, Math.min(100, (parseFloat(value) / parseFloat(max)) * 100));
  return (
    <li className="jury-row" data-testid={`story-jury-row-${index}`}>
      <div className="flex items-baseline justify-between gap-4 mb-1.5">
        <span className="text-sm text-foreground/85">{label}</span>
        <span className={`font-display text-sm whitespace-nowrap ${silver ? "text-zinc-300" : "gold-text"}`}>{value} / {max}</span>
      </div>
      <div className="jury-bar"><span className={`jury-bar-fill ${silver ? "jury-bar-fill--silver" : ""}`} style={{ "--w": `${pct}%`, transitionDelay: `${200 + index * 120}ms` }} /></div>
    </li>
  );
}

function JuryCard({ title, scores, note, tier, silver, index }) {
  const rows = splitLines(scores);
  if (rows.length === 0) return null;
  return (
    <Reveal delay={index * 180} className="kintsugi-card rounded-2xl p-7 lg:p-8 flex flex-col" data-testid={`story-jury-card-${index}`}>
      <div className="flex items-start justify-between gap-4 mb-1">
        <div className="smallcaps-label" style={{ fontSize: "0.62rem" }}>Frankfurt Trophy 2026</div>
        {tier && (
          <span className={`text-[10px] uppercase tracking-[0.22em] rounded-full border px-3 py-1 whitespace-nowrap ${silver ? "border-zinc-400/40 text-zinc-300" : "border-primary/40 text-primary"}`}>
            {tier}
          </span>
        )}
      </div>
      <h3 className="font-display text-xl md:text-2xl mb-7">{title}</h3>
      <ul className="grid gap-y-5 flex-1">
        {rows.map(([label, value, max], i) => <JuryBar key={i} index={i} label={label} value={value} max={max} silver={silver} />)}
      </ul>
      {note && <div className="mt-8 pt-5 border-t border-primary/15 text-xs text-muted-foreground tracking-wide" data-testid={`story-jury-note-${index}`}>{note}</div>}
    </Reveal>
  );
}

export default function StoryMedals({ medals, juries }) {
  const medalRows = splitLines(medals);
  if (medalRows.length === 0) return null;
  const cards = juries.filter((j) => j.scores);
  return (
    <div className="mt-6" data-testid="story-medals">
      <div className="kintsugi-divider mb-12" style={{ margin: "0 0 3rem 0", maxWidth: "8rem" }} />
      <div className="grid sm:grid-cols-2 gap-10 sm:gap-6 justify-items-center mb-14 max-w-2xl">
        {medalRows.map(([tier, product, score, tone], i) => (
          <Seal key={i} index={i} tier={tier} product={product} score={score} tone={tone} />
        ))}
      </div>

      {cards.length > 0 && (
        <div className={`grid gap-6 ${cards.length > 1 ? "lg:grid-cols-2" : ""}`} data-testid="story-jury-cards">
          {cards.map((j, i) => (
            <JuryCard key={i} index={i} title={j.title} scores={j.scores} note={j.note} tier={medalRows[i]?.[0]} silver={medalRows[i]?.[3] === "argent"} />
          ))}
        </div>
      )}
    </div>
  );
}
